import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { invoiceDocBadge, invoiceDisplayNumber } from '../utils/invoiceDoc';

// ExportInvoicesModal — the "send to accountant" export.
// Operator picks a period → previews totals → downloads CSV + prints a PDF summary.
// This is the bridge: our system holds the data, the accountant imports it into
// their official accounting software (Soft1 / Galaxy / Xero / QuickBooks).

const METHOD_LABEL = {
  cash: 'Cash', stripe: 'Card (Stripe)', viva: 'Card (Viva)', paypal: 'PayPal',
  online: 'Online', bank: 'Bank Transfer', paymentlink: 'Payment Link',
};

// Build a YYYY-MM key from an invoice's issue date (issueDate ISO preferred)
function invoiceMonthKey(inv) {
  const iso = inv.issueDate || inv.issuedAt;
  if (iso) return String(iso).slice(0, 7);
  // Fallback: try to parse the human "issue" label
  if (inv.issue) {
    const d = new Date(inv.issue);
    if (!isNaN(d)) return d.toISOString().slice(0, 7);
  }
  return '';
}

function monthLabel(key) {
  if (!key) return 'Undated';
  const [y, m] = key.split('-');
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

// CSV-safe field (wrap in quotes, escape internal quotes)
function csvCell(v) {
  const s = v == null ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function ExportInvoicesModal({ invoices = [], companySettings = {}, rentals = [], vehicles = [], onClose }) {
  // Available months (sorted desc), derived from the invoices we have
  const months = useMemo(() => {
    const set = new Set();
    invoices.forEach(i => { const k = invoiceMonthKey(i); if (k) set.add(k); });
    return [...set].sort().reverse();
  }, [invoices]);

  const [period, setPeriod] = useState(months[0] || '');
  const [includeDrafts, setIncludeDrafts] = useState(false);

  // Invoices in the selected period
  const rows = useMemo(() => {
    return invoices.filter(i => {
      if (!includeDrafts && i.status === 'draft') return false;
      return invoiceMonthKey(i) === period;
    });
  }, [invoices, period, includeDrafts]);

  // Totals
  const totals = useMemo(() => {
    const t = {
      count: rows.length,
      net: 0, vat: 0, gross: 0,
      byMethod: {}, byStatus: {},
      paidGross: 0, unpaidGross: 0,
    };
    rows.forEach(i => {
      const net = parseFloat(i.subtotal || 0);
      const vat = parseFloat(i.vatAmt ?? i.vat ?? 0);
      const gross = parseFloat(i.total || 0);
      t.net += net; t.vat += vat; t.gross += gross;
      t.byMethod[i.method] = (t.byMethod[i.method] || 0) + gross;
      t.byStatus[i.status] = (t.byStatus[i.status] || 0) + 1;
      if (i.status === 'paid') t.paidGross += gross; else t.unpaidGross += gross;
    });
    return t;
  }, [rows]);

  function buildCsv() {
    const header = [
      'Date', 'Document No', 'Type', 'Customer', 'Customer VAT',
      'Vehicle', 'Contract', 'Net', 'VAT Rate %', 'VAT Amount', 'Gross',
      'Payment Method', 'Status',
    ];
    const lines = [header.map(csvCell).join(',')];
    rows.forEach(i => {
      lines.push([
        i.issueDate || i.issue || '',
        invoiceDisplayNumber(i),
        invoiceDocBadge(i),
        i.customer || '',
        i.customerVatNumber || '',
        i.vehicle || '',
        i.contract || '',
        parseFloat(i.subtotal || 0).toFixed(2),
        parseFloat(i.vatRate || 0).toFixed(0),
        parseFloat(i.vatAmt ?? i.vat ?? 0).toFixed(2),
        parseFloat(i.total || 0).toFixed(2),
        METHOD_LABEL[i.method] || i.method || '',
        i.status || '',
      ].map(csvCell).join(','));
    });
    // Totals row
    lines.push('');
    lines.push([`TOTALS (${totals.count})`, '', '', '', '', '', '',
      totals.net.toFixed(2), '', totals.vat.toFixed(2), totals.gross.toFixed(2), '', ''].map(csvCell).join(','));
    return lines.join('\n');
  }

  function downloadCsv() {
    const csv = buildCsv();
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });  // BOM for Excel
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(companySettings.slug || 'fleetrent')}_proformas_${period}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function printSummary() {
    const co = companySettings.name || 'Company';
    const color = companySettings.brandColor || '#1A3A5C';
    const methodRows = Object.entries(totals.byMethod)
      .map(([m, amt]) => `<tr><td>${METHOD_LABEL[m] || m}</td><td style="text-align:right">€${amt.toFixed(2)}</td></tr>`)
      .join('');
    const detailRows = rows.map(i => `
      <tr>
        <td>${i.issueDate || i.issue || ''}</td>
        <td>${invoiceDisplayNumber(i)}</td>
        <td>${invoiceDocBadge(i)}</td>
        <td>${i.customer || ''}</td>
        <td style="text-align:right">€${parseFloat(i.subtotal||0).toFixed(2)}</td>
        <td style="text-align:right">€${parseFloat(i.vatAmt??i.vat??0).toFixed(2)}</td>
        <td style="text-align:right">€${parseFloat(i.total||0).toFixed(2)}</td>
        <td>${(METHOD_LABEL[i.method]||i.method||'')}</td>
        <td>${i.status||''}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Pro Forma Summary — ${monthLabel(period)}</title>
<style>
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;padding:30px 40px;}
  h1{font-size:22px;color:${color};margin-bottom:4px;}
  .sub{color:#666;margin-bottom:20px;font-size:13px;}
  table{width:100%;border-collapse:collapse;margin-bottom:24px;}
  th{background:${color};color:#fff;padding:7px 9px;text-align:left;font-size:11px;}
  th:not(:first-child){text-align:right;}
  td{padding:6px 9px;border-bottom:1px solid #eee;}
  tbody tr:nth-child(even){background:#f8f9fa;}
  .totbox{display:flex;gap:24px;margin-bottom:24px;}
  .totcard{flex:1;border:1px solid #ddd;border-radius:6px;padding:12px 16px;}
  .totcard .lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.05em;}
  .totcard .val{font-size:20px;font-weight:700;color:${color};margin-top:4px;}
  .small{font-size:10px;color:#999;margin-top:30px;border-top:1px solid #eee;padding-top:10px;}
  @media print{body{padding:0;}@page{margin:14mm;}}
</style></head><body>
  <h1>${co} — Pro Forma Summary</h1>
  <div class="sub">${monthLabel(period)} · ${totals.count} document${totals.count===1?'':'s'} · VAT ${companySettings.vatNum||''}</div>

  <div class="totbox">
    <div class="totcard"><div class="lbl">Net total</div><div class="val">€${totals.net.toFixed(2)}</div></div>
    <div class="totcard"><div class="lbl">VAT collected</div><div class="val">€${totals.vat.toFixed(2)}</div></div>
    <div class="totcard"><div class="lbl">Gross total</div><div class="val">€${totals.gross.toFixed(2)}</div></div>
  </div>

  <div class="totbox">
    <div class="totcard"><div class="lbl">Paid</div><div class="val" style="color:#10b981">€${totals.paidGross.toFixed(2)}</div></div>
    <div class="totcard"><div class="lbl">Outstanding</div><div class="val" style="color:#f59e0b">€${totals.unpaidGross.toFixed(2)}</div></div>
  </div>

  <h3 style="font-size:13px;color:${color};">By payment method</h3>
  <table><tbody>${methodRows}</tbody></table>

  <h3 style="font-size:13px;color:${color};">Detail</h3>
  <table>
    <thead><tr><th>Date</th><th>No.</th><th>Type</th><th>Customer</th><th>Net</th><th>VAT</th><th>Gross</th><th>Method</th><th>Status</th></tr></thead>
    <tbody>${detailRows}</tbody>
  </table>

  <div class="small">
    Generated by FleetRent CRM on ${new Date().toLocaleDateString('en-GB')}.
    This summary is for bookkeeping reference. Official tax invoices / receipts are issued separately.
    VAT figures reflect output VAT only (sales). Input VAT (expenses) handled separately by your accountant.
  </div>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  }

  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--sur)', borderRadius: 'var(--rl)', width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bl-l)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📥</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Export for Accountant</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Monthly pro forma package — CSV + printable summary</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 18, overflowY: 'auto' }}>
          {months.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--tx3)', fontSize: 12 }}>
              No pro formas to export yet.
            </div>
          ) : (
            <>
              {/* Period picker */}
              <div className="fg" style={{ marginBottom: 12 }}>
                <label className="fl">Billing period</label>
                <select className="fi" value={period} onChange={e => setPeriod(e.target.value)}>
                  {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--tx2)', marginBottom: 16, cursor: 'pointer' }}>
                <input type="checkbox" checked={includeDrafts} onChange={e => setIncludeDrafts(e.target.checked)} />
                Include drafts (unissued)
              </label>

              {/* Totals preview */}
              <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
                  {monthLabel(period)} · {totals.count} document{totals.count === 1 ? '' : 's'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div><div style={{ fontSize: 10, color: 'var(--tx3)' }}>Net</div><div style={{ fontSize: 16, fontWeight: 700 }}>€{totals.net.toFixed(2)}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--tx3)' }}>VAT</div><div style={{ fontSize: 16, fontWeight: 700 }}>€{totals.vat.toFixed(2)}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--tx3)' }}>Gross</div><div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gr)' }}>€{totals.gross.toFixed(2)}</div></div>
                </div>
                <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--tx2)', borderTop: '1px solid var(--bd)', paddingTop: 8 }}>
                  <span>✅ Paid: <strong style={{ color: 'var(--gr)' }}>€{totals.paidGross.toFixed(2)}</strong></span>
                  <span>⏳ Outstanding: <strong style={{ color: 'var(--am)' }}>€{totals.unpaidGross.toFixed(2)}</strong></span>
                </div>
              </div>

              <div style={{ fontSize: 10, color: 'var(--tx3)', lineHeight: 1.6, marginBottom: 4 }}>
                The CSV opens in Excel / Google Sheets and imports into most accounting software.
                The summary is a printable PDF overview. VAT shown is output VAT (sales) only.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--bd)' }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-ghost" onClick={printSummary} disabled={totals.count === 0}>🖨️ Print Summary</button>
          <button className="btn btn-primary" onClick={downloadCsv} disabled={totals.count === 0}>📄 Download CSV</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
